import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { MenuItem, Select, Tooltip } from "@mui/material";
import {
  FaChevronLeft,
  FaChevronRight,
  FaRegCalendarAlt,
  FaSearch,
  FaTicketAlt,
  FaUserCircle,
} from "react-icons/fa";
import moment from "moment-jalali";

import service from "../../../core/service";
import { calculateNext, calculatePrev } from "../../../core/utils";
import { Cancel } from "../../../assets";
import { Input, Layout, LoadingSpinner } from "../../components";
import { getDaysOfMonth } from "../../components/datePicker/utils/moment-helper";
import { persianNumber } from "../../components/datePicker/utils/persian";
import { ticketDisplaySubject } from "./ticketSubject";

moment.loadPersian();

const MONTH_NAMES = [
  "فروردین",
  "اردیبهشت",
  "خرداد",
  "تیر",
  "مرداد",
  "شهریور",
  "مهر",
  "آبان",
  "آذر",
  "دی",
  "بهمن",
  "اسفند",
];
const WEEKDAYS = [
  "شنبه",
  "یکشنبه",
  "دوشنبه",
  "سه‌شنبه",
  "چهارشنبه",
  "پنجشنبه",
  "جمعه",
];

interface CalendarTicket {
  date: string;
  subject: string;
  registrar: string;
  ticket_id: number;
  ticket_subject?: string;
  lead_state_name?: string;
  deal_state_name?: string;
  deal_stage_name?: string;
}

// module-level caches so back-navigation never refetches the calendar request
let calendarCache: { key: string; list: CalendarTicket[] } | null = null;
let holidayCache: { key: string; list: { date: string }[] } | null = null;
let inFlight: { key: string; promise: Promise<unknown> } | null = null;

function CalendarPage() {
  const navigate = useNavigate();
  const today = moment();

  const [year, setYear] = useState<number>(Number(today.format("jYYYY")));
  const [month, setMonth] = useState<number>(Number(today.format("jM")));
  const [taskList, setTaskList] = useState<CalendarTicket[]>([]);
  const [holidayList, setHolidayList] = useState<{ date: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const reqRef = useRef(0);

  // ticket-title search (debounced 300ms — pure frontend filter over the loaded month data)
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const monthMoment = useMemo(
    () => moment(`${year}/${month}/15`, "jYYYY/jM/jDD"),
    [year, month]
  );

  const fetchCalendar = useCallback(async (y: number, m: number) => {
    const key = `${y}/${m}`;

    // cache hit → render instantly, no network request
    if (calendarCache && calendarCache.key === key) {
      setTaskList(calendarCache.list);
      setHolidayList(holidayCache?.key === key ? holidayCache.list : []);
      setLoading(false);
      return;
    }

    // the exact same month request is already in flight (StrictMode double-effect, re-renders) — reuse it
    if (inFlight && inFlight.key === key) {
      try {
        await inFlight.promise;
      } catch {
        /* handled by the original caller */
      }
      if (calendarCache?.key === key) {
        setTaskList(calendarCache.list);
        setHolidayList(holidayCache?.key === key ? holidayCache.list : []);
      }
      setLoading(false);
      return;
    }

    const id = ++reqRef.current;
    setLoading(true);
    const target = moment(`${y}/${m}/15`, "jYYYY/jM/jDD");
    // tickets + holidays in parallel — a single network round;
    // each request fails gracefully on its own so one bad endpoint never wipes the other's data
    const promise = Promise.all([
      service.Calender.list(calculatePrev(target), calculateNext(target)).catch(() => null),
      service.Calender.holiday(calculatePrev(target), calculateNext(target)).catch(() => null),
    ]).then(([tickRes, holRes]) => ({
      list: tickRes?.data?.data?.list || [],
      holidayList: holRes?.data?.data?.list || [],
    }));
    inFlight = { key, promise };

    try {
      const res = await promise;
      if (id !== reqRef.current) return; // stale response — a newer month was requested
      calendarCache = { key, list: res.list };
      holidayCache = { key, list: res.holidayList };
      setTaskList(res.list);
      setHolidayList(res.holidayList);
    } catch {
      if (id === reqRef.current) {
        setTaskList([]);
        setHolidayList([]);
      }
    } finally {
      // clear only the exact request that finished (identity check — never clobber a newer one)
      if (inFlight?.promise === promise) inFlight = null;
      if (id === reqRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCalendar(year, month);
  }, [year, month, fetchCalendar]);

  const days = useMemo(() => getDaysOfMonth(monthMoment), [monthMoment]);
  const todayKey = today.format("jYYYY/jMM/jDD");
  const currentMonthKey = monthMoment.format("jYYYYjMM");

  const searchActive = search.length > 0;

  const filteredTickets = useMemo(() => {
    if (!searchActive) return taskList;
    const q = search.toLowerCase();
    return taskList.filter((t) =>
      ticketDisplaySubject(t).toLowerCase().includes(q)
    );
  }, [taskList, search, searchActive]);

  // full (unfiltered) tickets per day — the search only narrows the grid view,
  // never the day page: clicking a day always opens ALL its tickets
  const allTicketsByDate = useMemo(() => {
    const map: Record<string, CalendarTicket[]> = {};
    taskList.forEach((t) => {
      (map[t.date] = map[t.date] || []).push(t);
    });
    return map;
  }, [taskList]);

  // filtered tickets per day — drives the boxes/badge shown on the grid
  const ticketsByDate = useMemo(() => {
    const map: Record<string, CalendarTicket[]> = {};
    filteredTickets.forEach((t) => {
      (map[t.date] = map[t.date] || []).push(t);
    });
    return map;
  }, [filteredTickets]);

  const holidaySet = useMemo(
    () => new Set(holidayList.map((h) => h.date)),
    [holidayList]
  );

  const openDay = (dateKey: string, tickets: CalendarTicket[]) => {
    // every day opens the day page — empty days render the kanban empty state
    const [y, m, d] = dateKey.split("/");
    navigate(`/calendar/${y}/${m}/${d}`, { state: { tickets: tickets || [], date: dateKey } });
  };

  const shiftMonth = (dir: number) => {
    let y = year;
    let m = month + dir;
    if (m > 12) {
      m = 1;
      y += 1;
    }
    if (m < 1) {
      m = 12;
      y -= 1;
    }
    setYear(y);
    setMonth(m);
  };

  const goToday = () => {
    setYear(Number(today.format("jYYYY")));
    setMonth(Number(today.format("jM")));
  };

  const currentYear = Number(today.format("jYYYY"));
  const years = Array.from({ length: 16 }, (_, i) => currentYear - 8 + i);

  const renderTooltip = (tickets: CalendarTicket[]) => (
    <div className="calendar-tooltip">
      <div className="calendar-tooltip-head">
        <FaTicketAlt /> {persianNumber(tickets.length)} تیکت
      </div>
      {tickets.map((t) => (
        <div key={t.ticket_id} className="calendar-tooltip-item">
          <span className="calendar-tooltip-subject">{ticketDisplaySubject(t)}</span>
          <span className="calendar-tooltip-registrar">
            <FaUserCircle /> {t.registrar || "—"}
          </span>
        </div>
      ))}
    </div>
  );

  const renderDay = (day: any) => {
    const dateKey = day.format("jYYYY/jMM/jDD");
    const tickets = ticketsByDate[dateKey] || [];
    const isCurrent = day.format("jYYYYjMM") === currentMonthKey;
    const isToday = dateKey === todayKey;
    const isHoliday = holidaySet.has(dateKey);
    const classes = [
      "calendar-day",
      !isCurrent && "is-outside",
      isToday && "is-today",
      isHoliday && "is-holiday",
      tickets.length > 0 && "has-tickets",
      "clickable",
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <Tooltip
        key={dateKey}
        title={tickets.length > 0 ? renderTooltip(tickets) : ""}
        enterDelay={500}
        arrow
        placement="top"
        classes={{ tooltip: "calendar-tooltip-box" }}
      >
        <div
          className={classes}
          onClick={() => openDay(dateKey, allTicketsByDate[dateKey] || [])}
        >
          <span className="calendar-day-num">{persianNumber(day.format("jD"))}</span>
          {searchActive ? (
            tickets.length > 0 && (
              <div className="calendar-day-boxes">
                {tickets.map((t, i) => (
                  <div
                    key={t.ticket_id}
                    className="calendar-day-box"
                    title={ticketDisplaySubject(t)}
                    style={{ animationDelay: `${Math.min(i, 8) * 30}ms` }}
                  >
                    <FaTicketAlt />
                    <span className="calendar-day-box-subject">{ticketDisplaySubject(t)}</span>
                  </div>
                ))}
              </div>
            )
          ) : (
            tickets.length > 0 && (
              <span className="calendar-day-badge">{persianNumber(tickets.length)}</span>
            )
          )}
        </div>
      </Tooltip>
    );
  };

  return (
    <Layout title="تقویم">
      <Helmet>
        <title>تقویم | سامانه مشتریان</title>
      </Helmet>
      <div className="calendar-page">
        <div className="calendar-toolbar">
          <div className="calendar-toolbar-nav">
            <button
              type="button"
              className="calendar-nav-btn"
              onClick={() => shiftMonth(-1)}
              title="ماه قبل"
            >
              <FaChevronRight />
            </button>
            <div className="calendar-month-title">
              <FaRegCalendarAlt />
              <span>{persianNumber(monthMoment.format("jMMMM jYYYY"))}</span>
            </div>
            <button
              type="button"
              className="calendar-nav-btn"
              onClick={() => shiftMonth(1)}
              title="ماه بعد"
            >
              <FaChevronLeft />
            </button>
            <button type="button" className="calendar-today-btn" onClick={goToday}>
              امروز
            </button>
          </div>

          <div className="calendar-toolbar-filters">
            <Select
              className="calendar-select"
              value={month}
              onChange={(e: any) => setMonth(Number(e.target.value))}
            >
              {MONTH_NAMES.map((name, i) => (
                <MenuItem key={name} value={i + 1}>
                  {name}
                </MenuItem>
              ))}
            </Select>
            <Select
              className="calendar-select"
              value={year}
              onChange={(e: any) => setYear(Number(e.target.value))}
            >
              {years.map((y) => (
                <MenuItem key={y} value={y}>
                  {persianNumber(y)}
                </MenuItem>
              ))}
            </Select>
            <div className="calendar-search">
              <Input
                myStyle="calendar-search-input"
                icon={<FaSearch className="calendar-search-icon" />}
                placeholder="جستجوی عنوان تیکت…"
                value={searchInput}
                onChange={(e: any) => setSearchInput(e.target.value)}
                endAdornment={
                  searchInput ? (
                    <Cancel
                      myStyle="cursor-pointer"
                      width="14"
                      height="14"
                      color="#8a94a6"
                      onClick={() => {
                        setSearchInput("");
                        setSearch("");
                      }}
                    />
                  ) : undefined
                }
              />
            </div>
          </div>
        </div>

        <div className="calendar-card">
          <div className="calendar-weekdays">
            {WEEKDAYS.map((w) => (
              <div key={w} className="calendar-weekday">
                {w}
              </div>
            ))}
          </div>
          {loading ? (
            <div className="calendar-loading">
              <LoadingSpinner />
            </div>
          ) : (
            <div className="calendar-grid">{days.map(renderDay)}</div>
          )}
        </div>
      </div>
    </Layout>
  );
}

export default CalendarPage;
